import {
	getEntityRanges,
	BLOCK_TYPE,
	ENTITY_TYPE,
	INLINE_STYLE,
} from 'draft-js-utils';

import { ContentState, ContentBlock, convertFromRaw } from 'draft-js';

const { BOLD, CODE, ITALIC, STRIKETHROUGH, UNDERLINE } = INLINE_STYLE;

const CODE_INDENT = '    ';

class MarkupGenerator {
	/** @type {ContentBlock[]} */
	blocks;

	/** @type {ContentState} */
	contentState;

	/** @type {number} */
	currentBlock;

	/** @type {string[]} */
	output;

	/** @type {number} */
	totalBlocks;

	/** @type {Object} */
	listItemCounts;

	constructor(contentState) {
		this.contentState = contentState;
	}

	generate() {
		this.output = [];
		this.blocks = this.contentState.getBlockMap().toArray();
		this.totalBlocks = this.blocks.length;
		this.currentBlock = 0;
		this.listItemCounts = {};
		while (this.currentBlock < this.totalBlocks) {
			this.processBlock();
		}
		return this.output.join('');
	}

	processBlock() {
		let block = this.blocks[this.currentBlock];
		let blockType = block.getType();
		switch (blockType) {
			case BLOCK_TYPE.HEADER_ONE: {
				this.insertLineBreaks(1);
				this.output.push('# ' + this.renderBlockContent(block) + '\n');
				break;
			}
			case BLOCK_TYPE.HEADER_TWO: {
				this.insertLineBreaks(1);
				this.output.push('## ' + this.renderBlockContent(block) + '\n');
				break;
			}
			case BLOCK_TYPE.HEADER_THREE: {
				this.insertLineBreaks(1);
				this.output.push('### ' + this.renderBlockContent(block) + '\n');
				break;
			}
			case BLOCK_TYPE.HEADER_FOUR: {
				this.insertLineBreaks(1);
				this.output.push('#### ' + this.renderBlockContent(block) + '\n');
				break;
			}
			case BLOCK_TYPE.HEADER_FIVE: {
				this.insertLineBreaks(1);
				this.output.push('##### ' + this.renderBlockContent(block) + '\n');
				break;
			}
			case BLOCK_TYPE.HEADER_SIX: {
				this.insertLineBreaks(1);
				this.output.push('###### ' + this.renderBlockContent(block) + '\n');
				break;
			}
			case BLOCK_TYPE.UNORDERED_LIST_ITEM: {
				let blockDepth = block.getDepth();
				let lastBlock = this.getLastBlock();
				let lastBlockType = lastBlock ? lastBlock.getType() : null;
				let lastBlockDepth =
					lastBlock && canHaveDepth(lastBlockType)
						? lastBlock.getDepth()
						: null;
					if (lastBlockType !== BLOCK_TYPE.ORDERED_LIST_ITEM
						&& lastBlockType !== BLOCK_TYPE.UNORDERED_LIST_ITEM
						&& lastBlockDepth !== blockDepth - 1
					) {
					this.insertLineBreaks(1);
					// Insert an additional line break if following opposite list type.
					if (lastBlockType === BLOCK_TYPE.ORDERED_LIST_ITEM) {
						this.insertLineBreaks(1);
					}
				}
				let indent = ' '.repeat(block.depth * 4);
				this.output.push(indent + '- ' + this.renderBlockContent(block) + '\n');
				break;
			}
			case BLOCK_TYPE.ORDERED_LIST_ITEM: {
				let blockDepth = block.getDepth();
				let lastBlock = this.getLastBlock();
				let lastBlockType = lastBlock ? lastBlock.getType() : null;
				let lastBlockDepth =
					lastBlock && canHaveDepth(lastBlockType)
						? lastBlock.getDepth()
						: null;
				if (lastBlockType !== BLOCK_TYPE.ORDERED_LIST_ITEM
					&& lastBlockType !== BLOCK_TYPE.UNORDERED_LIST_ITEM
					&& lastBlockDepth !== blockDepth - 1
				) {
					this.insertLineBreaks(1);
					// Insert an additional line break if following opposite list type.
					if (lastBlockType === BLOCK_TYPE.UNORDERED_LIST_ITEM) {
						this.insertLineBreaks(1);
					}
				}
				let indent = ' '.repeat(blockDepth * 4);
				// TODO: figure out what to do with two-digit numbers
				// 原作者对下面的 count 结果进行了 % 10 操作，目前不知道有何作用，去除没发现有问题
				let count = this.getListItemCount(block);
				this.output.push(
					indent + `${count}. ` + this.renderBlockContent(block) + '\n',
				);
				break;
			}
			case BLOCK_TYPE.BLOCKQUOTE: {
				let lastBlock = this.getLastBlock();
				let lastBlockType = lastBlock ? lastBlock.getType() : null;
				if (lastBlockType !== BLOCK_TYPE.BLOCKQUOTE) {
					this.insertLineBreaks(1);
				}
				const quote = this.renderBlockContent(block);
				for (let line of quote.split(/\n\r?/g)) {
					this.output.push('> ' + line + '\n');
				}
				break;
			}
			case BLOCK_TYPE.CODE: {
				this.insertLineBreaks(1);
				const syntax = block.getData && block.getData() && block.getData().get('syntax') || '';
				this.output.push('```' + syntax + '\n');
				this.output.push(block.getText() + '\n');
				this.output.push('```' + '\n');
				break;
			}
			case 'table': {
				this.insertLineBreaks(1);
				const data = block.getData();
				const aligns = data.get('aligns');
				const rows = data.get('rows').slice();

				const isTableContainCombinedCell = (rows) => {
					for (let row of rows) {
						for (let cell of row.cells) {
							if (cell.colspan > 1 || cell.rowspan > 1) {
								return true;
							}
						}
					}
					return false;
				}
				const getCellContent = (cell) => {
					if (!cell.raw) {
						return '';
					}
					const contentState = convertFromRaw(cell.raw);
					const content = this.renderBlockContent(contentState.getFirstBlock(), contentState);
					return encodeTableCell(content);
				};
				const outputUsingMarkdown = () => {
					const getRowContent = (row) => {
						return '| ' + row.cells.map(getCellContent).join(' | ') + ' |';
					};
					const getAlignContent = (aligns) => {
						const spliter = aligns.map(({ align }) => {
							switch (align) {
								case 'right': return '----:';
								case 'center': return ':---:';
								case 'left': default: return ':----';
							}
						}).join('|');
						return '|' + spliter + '|';stt
					}
					this.output.push(getRowContent(rows.shift()) + '\n');
					this.output.push(getAlignContent(aligns) + '\n');
					for (let row of rows) {
						this.output.push(getRowContent(row) + '\n');
					}
				}
				const outputUsingTable = () => {
					this.output.push('<table>\n');
					let firstRow = true;
					for (let row of rows) {
						this.output.push(`  <tr>\n`);
						let j = 0;
						for (let cell of row.cells) {
							let properties = '';
							if (cell.rowspan > 1) {
								properties += ` rowspan="${cell.rowspan}"`;
							}
							if (cell.colspan > 1) {
								properties += ` colspan="${cell.colspan}"`;
							}
							if (cell.raw) {
								let tag = 'td';
								if (firstRow) {
									tag = 'th';
									properties += ` class="align-${aligns[j] && aligns[j].align || 'left'}"`;
								}
								this.output.push(`    <${tag}${properties}>${getCellContent(cell)}</${tag}>\n`);
							}
							++j;
						}
						firstRow = false;
						this.output.push(`  </tr>\n`);
					}
					this.output.push('</table>\n');
				}
				// 包含合并单元格的表格无法导出成 Markdown 格式
				if (isTableContainCombinedCell(rows)) {
					outputUsingTable();
				}
				else {
					outputUsingMarkdown();
				}
				this.insertLineBreaks();
				break;
			}
			default: {
				this.insertLineBreaks(1);
				this.output.push(this.renderBlockContent(block) + '\n');
				break;
			}
		}
		this.currentBlock += 1;
	}

	getLastBlock() {
		return this.blocks[this.currentBlock - 1];
	}

	getNextBlock() {
		return this.blocks[this.currentBlock + 1];
	}

	/**
	 * @param {ContentBlock} block
	 * @returns {number}
	 */
	getListItemCount(block) {
		let blockType = block.getType();
		let blockDepth = block.getDepth();
		// To decide if we need to start over we need to backtrack (skipping list
		// items that are of greater depth)
		let index = this.currentBlock - 1;
		let prevBlock = this.blocks[index];
		while (
			prevBlock &&
			canHaveDepth(prevBlock.getType()) &&
			prevBlock.getDepth() > blockDepth
		) {
			index -= 1;
			prevBlock = this.blocks[index];
		}
		if (
			!prevBlock ||
			prevBlock.getType() !== blockType ||
			prevBlock.getDepth() !== blockDepth
		) {
			this.listItemCounts[blockDepth] = 0;
		}
		return (this.listItemCounts[blockDepth] =
			this.listItemCounts[blockDepth] + 1);
	}

	insertLineBreaks() {
		if (this.currentBlock > 0) {
			this.output.push('\n');
		}
	}

	/**
	 * @param {ContentBlock} block
	 * @returns {string}
	 */
	renderBlockContent(block, contentState = this.contentState) {
		let blockType = block.getType();

		// 公式 atomic
		if (blockType === 'atomic' && block.getData() && block.getData().get('mathjax')) {
			const teX = block.getData().get('teX');
			return `> \`$${teX}$\``;
		}

		let text = block.getText();
		if (text === '') {
			// Prevent element collapse if completely empty.
			// TODO: Replace with constant.
			return '\u200B';
		}
		let charMetaList = block.getCharacterList();
		let entityPieces = getEntityRanges(text, charMetaList);
		return entityPieces
			.map(([entityKey, stylePieces]) => {
				let content = stylePieces
					.map(([text, style]) => {
						// Don't allow empty inline elements.
						if (!text) {
							return '';
						}
						if (style.has(CODE)) {
							content = text.replace(/`/g, '&grave;');
							content = blockType === BLOCK_TYPE.CODE ? content : addStyleInsideLeadingSpace(content, '`');
						}
						else {
							content = encodeContent(text)
						}
						if (style.has(BOLD)) {
							content = addStyleInsideLeadingSpace(content, '**');
						}
						// if (style.has(UNDERLINE)) {
						// 	// TODO: encode `+`?
						// 	content = addStyleInsideLeadingSpace(content, '**');
						// 	content = `++${content}++`;
						// }
						if (style.has(ITALIC)) {
							content = addStyleInsideLeadingSpace(content, '_');
						}
						if (style.has(STRIKETHROUGH)) {
							content = addStyleInsideLeadingSpace(content, '~~');
						}
						return content;
					})
					.join('');
				let entity = entityKey ? contentState.getEntity(entityKey) : null;
				if (entity != null && entity.getType() === ENTITY_TYPE.LINK) {
					let data = entity.getData();
					let url = data.url || '';
					let title = data.title ? ` "${escapeTitle(data.title)}"` : '';
					return `[${content}](${encodeURL(url)}${title})`;
				}
				else if (entity != null && entity.getType() === ENTITY_TYPE.IMAGE) {
					let data = entity.getData();
					let src = data.imageUrl || '';
					let alt = data.name || '';
					return src ? `![${alt}](${encodeURL(src)})` : `[${alt || '图片'}]`;
				}
				else if (entity != null && entity.getType() === 'GALLERY') {
					return generateGallery(entity.getData());
				}
				else if (entity != null && entity.getType() === 'ATTACHMENT') {
					return generateAttachment(entity.getData());
				}
				else if (entity != null && entity.getType() === 'VIDEO') {
					return generateVideo(entity.getData());
				}
				else if (entity !== null && entity.getType() === 'INLINETEX') {
					return generateTex(entity.getData());
				}
				else {
					return content;
				}
			})
			.join('');
	}
}

const generateGallery = ({ name, base, images }) => `\`\`\`gallery
${JSON.stringify({
	$galleryVersion: '1.0',
	name, base, images,
})}
\`\`\``;

const generateAttachment = ({ name, url }) => `> [#${name}#](${url})`;

const generateVideo = ({ name, videoPlatform, videoId }) => `> ![${name}](video://${videoPlatform}/${videoId})`;

const generateTex = ({ teX }) => `\`$${teX}$\``;

/**
 * addStyleInsideLeadingSpace(' box', '**') => ' **box**'
 * addStyleInsideLeadingSpace('good dog  ', '~~') => '~~good dog~~  '
 */
function addStyleInsideLeadingSpace(content, markup) {
	return content.replace(/^(\s*)(.*?)(\s*)$/, `$1${markup}$2${markup}$3`);
}

function canHaveDepth(blockType) {
	switch (blockType) {
		case BLOCK_TYPE.UNORDERED_LIST_ITEM:
		case BLOCK_TYPE.ORDERED_LIST_ITEM:
			return true;
		default:
			return false;
	}
}

function encodeContent(text, useBr) {
	return text.replace(/[*_]/g, '\\$&');
}

function encodeXMLAttr(value, preserveCR = true) {
	// @see https://stackoverflow.com/questions/7753448/how-do-i-escape-quotes-in-html-attribute-values
	preserveCR = preserveCR ? '&#13;' : '\n';
	return ('' + value) /* Forces the conversion to string. */
			.replace(/&/g, '&amp;') /* This MUST be the 1st replacement. */
			.replace(/'/g, '&apos;') /* The 4 other predefined entities, required. */
			.replace(/"/g, '&quot;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			/*
			You may add other replacements here for HTML only
			(but it's not necessary).
			Or for XML, only if the named entities are defined in its DTD.
			*/
			.replace(/\r\n/g, preserveCR) /* Must be before the next replacement. */
			.replace(/[\r\n]/g, preserveCR);
			;
}

// Encode chars that would normally be allowed in a URL but would conflict with
// our markdown syntax: `[foo](http://foo/)`
function encodeURL(url) {
	return url.replace(/\)/g, '%29');
}

// Escape quotes using backslash.
function escapeTitle(text) {
	return text.replace(/"/g, '\\"');
}

function encodeTableCell(text) {
	return text.replace(/\|/g, '&#124;').replace(/\r?\n/g, '<br>');
}

/**
 * @param {ContentState} content
 */
export function convertDraftContentToMarkdown(content) {
	const markdown = new MarkupGenerator(content).generate();
	return markdown.replace(/^(\s*)|(\s*$)/g, '');
}



// WEBPACK FOOTER //
// ./lib/convert/markdown/convertDraftContentToMarkdown.js